import type {
  MetaGoalBucket,
  PortalMetaGoalPanel,
  PortalMetaGoalReport,
  PortalReportLabels,
} from '../../../../../types/portalReport';
import {
  GOAL_CHART_HISTORY_NOTE,
  GOAL_FILTER_NOTE,
  META_GOAL_COST_PER_LABELS,
  META_GOAL_COUNT_LABELS,
  META_GOAL_FILTER_OPTIONS,
  filterableGoalBucket,
  panelHasActivity,
  panelSpecFor,
  type MetaGoalPanelSpec,
} from '../../../../../lib/portalReport/metaGoalReport';
import { Band, Figure, SectionHeading, SwitchGroup } from './controls';
import { ReportChartCard } from './ReportChartCard';
import { ReportHero, type HeroStat } from './ReportHero';
import { DASH, formatReportDate, moneyFull, numberFull } from './format';

// CLIENT PORTAL RESTYLE — the goal-split Meta page (2026-09-24; round 2
// 2026-09-25).
//
// ⚠️⚠️ WHAT EACH PANEL SHOWS IS `META_GOAL_PANELS` in
// src/lib/portalReport/metaGoalReport.ts; this file only draws it. A cost-per
// that is not in a panel's spec is not drawn, because it is not in the payload.
//
// ⚠️ ROAS APPEARS NOWHERE (Scott). ⚠️ No platform logos, as elsewhere.

/**
 * The headline — conversions, top of the page, in the page's hero band.
 *
 * ⚠️⚠️ META ONLY (round 2). There is no Google stat and no Google word here, and
 * the headline is Meta leads + Meta purchases — the four stats below it are
 * Meta's own. ⚠️ Impressions are summed; reach never is (it counts people).
 */
export function MetaGoalHeadline({
  goal,
  range,
}: {
  goal: PortalMetaGoalReport;
  range: { from: string; to: string };
}) {
  const h = goal.headline;
  const stat = (label: string, value: number): HeroStat => ({
    label,
    value: numberFull(value),
    delta: null,
    metric: 'leads',
    unavailable: null,
  });
  return (
    <ReportHero
      eyebrow={`Conversions · ${formatReportDate(range.from)} – ${formatReportDate(range.to)}`}
      headline={numberFull(h.total)}
      intro="Meta leads and Meta purchases for this period, each counted once."
      stats={[
        stat('Meta leads', h.metaLeads),
        stat('Meta purchases', h.metaPurchases),
        stat('Landing page views', h.metaLandingPageViews),
        stat('Impressions', h.metaImpressions),
      ]}
    />
  );
}

function countValue(panel: PortalMetaGoalPanel, key: keyof PortalMetaGoalPanel['counts']): string {
  return numberFull(panel.counts[key]);
}

function PanelChart({
  panel,
  labels,
}: {
  panel: PortalMetaGoalPanel;
  labels: PortalReportLabels;
}) {
  const series = panel.series;
  if (!series) return null;
  const metricLabel = META_GOAL_COUNT_LABELS[series.metric];
  return (
    <ReportChartCard
      // ⚠️ The panel's SUBJECT, not the metric — the metric chips change what is
      // plotted, and a title naming one metric goes stale on the first click.
      title="Performance over time"
      source={{ kind: 'goal', period: series.period, months: series.months }}
      // ⚠️ Spend and the cost-per only when the series may carry money (one
      // currency and a cost-per for this panel's goal) — see `costPer`.
      metrics={series.costPer ? ['leads', 'costPerLead', 'spend'] : ['leads']}
      // ⚠️ The count rides in `leads` and the cost-per in `costPerLead` (see
      // PortalMetaGoalPanel.series), so the chart is told what it is really
      // plotting. The chips AND the accessible name read these — without the
      // override a Purchase chart would say "Ad Conv." and "Cost per lead".
      labels={{
        ...labels,
        leads: metricLabel,
        leadsShort: metricLabel,
        ...(series.costPer ? { costPerLead: META_GOAL_COST_PER_LABELS[series.costPer] } : {}),
      }}
      currency={series.currency ?? panel.money[0]?.currency ?? 'AUD'}
      periodLabel="this period"
    />
  );
}

function PanelCard({
  panel,
  spec,
  labels,
}: {
  panel: PortalMetaGoalPanel;
  spec: MetaGoalPanelSpec;
  labels: PortalReportLabels;
}) {
  const active = panelHasActivity(panel);
  const money = panel.money.length > 0 ? panel.money : null;
  return (
    <section className="rpt-card" aria-label={spec.title} data-goal-bucket={panel.bucket}>
      <h3 className="rpt-h3 rpt-display">{spec.title}</h3>
      {/* ⚠️ The Uncategorised line renders WHENEVER the panel has anything in it,
          so a franchisee seeing a large share of their spend here reads why
          before assuming the worst. It is also what makes that panel's missing
          chart read as deliberate. */}
      {spec.note && active && <p className="rpt-note rpt-muted">{spec.note}</p>}
      <div className="rpt-stat-grid">
        {spec.primary.map((k) => (
          <p key={k} className="rpt-stat">
            <Figure value={countValue(panel, k)} className="rpt-card-figure" />
            <span className="rpt-mono rpt-stat-label">{META_GOAL_COUNT_LABELS[k]}</span>
          </p>
        ))}
        {spec.costPer.map((k) => (
          <p key={k} className="rpt-stat">
            <Figure
              value={
                money
                  ? money
                      .map((m) => {
                        const v = m.costPer[k];
                        return v === null || v === undefined ? DASH : moneyFull(v, m.currency);
                      })
                      .join(' · ')
                  : null
              }
              className="rpt-card-figure"
            />
            <span className="rpt-mono rpt-stat-label">{META_GOAL_COST_PER_LABELS[k]}</span>
          </p>
        ))}
        {spec.showPurchaseValue && (
          <p className="rpt-stat">
            <Figure
              value={money ? money.map((m) => moneyFull(m.purchaseValue, m.currency)).join(' · ') : null}
              className="rpt-card-figure"
            />
            <span className="rpt-mono rpt-stat-label">Purchase value</span>
          </p>
        )}
        <p className="rpt-stat">
          <Figure
            // ⚠️ No rows = nothing ran in this bucket: a real zero, not unmeasured —
            // but with no currency to print it in, so a bare 0.
            value={money ? money.map((m) => moneyFull(m.spend, m.currency)).join(' · ') : '0'}
            className="rpt-card-figure"
          />
          <span className="rpt-mono rpt-stat-label">Spend</span>
        </p>
      </div>
      {spec.extras.length > 0 && (
        <p className="rpt-sub rpt-muted" data-goal-extras>
          {/* ⚠️ Extras are NUMBERS, not efficiency metrics — no cost-per here. */}
          Also from these campaigns:{' '}
          {spec.extras.map((k) => `${countValue(panel, k)} ${META_GOAL_COUNT_LABELS[k].toLowerCase()}`).join(' · ')}
        </p>
      )}
      <div style={{ marginTop: 14 }}>
        <PanelChart panel={panel} labels={labels} />
      </div>
    </section>
  );
}

/** The four panels, in spec order. */
export function MetaGoalPanels({
  goal,
  labels,
}: {
  goal: PortalMetaGoalReport;
  labels: PortalReportLabels;
}) {
  const charted = goal.panels.some((p) => p.series !== null);
  return (
    <Band>
      <SectionHeading
        title="Results by campaign goal"
        intro="Each campaign is counted in one panel. Cost figures are shown only for the goal each panel was set up for."
      />
      {/* ⚠️⚠️ STATED ONCE, ABOVE THE CHARTS, because the dip it explains looks
          like a fault to anyone not expecting it — see GOAL_CHART_HISTORY_NOTE. */}
      {charted && <p className="rpt-note rpt-muted">{GOAL_CHART_HISTORY_NOTE}</p>}
      <div className="rpt-card-grid rpt-card-grid--2">
        {goal.panels.map((p) => (
          <PanelCard key={p.bucket} panel={p} spec={panelSpecFor(p.bucket)} labels={labels} />
        ))}
      </div>
    </Band>
  );
}

/**
 * The goal filter's button row — rendered in the `aside` of BOTH the audience
 * and the creative-grid headings, over ONE shared state, so the two sections
 * cannot end up filtered differently.
 *
 * ⚠️ Four buttons; Uncategorised has none (Scott). The note travels with the
 * control so "All Campaigns" never reads as the sum of the other three.
 */
export function MetaGoalFilterButtons({
  value,
  onChange,
}: {
  value: MetaGoalBucket | null;
  onChange: (next: MetaGoalBucket | undefined) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
      <SwitchGroup
        ariaLabel="Campaign goal"
        value={filterableGoalBucket(value) ?? 'all'}
        onChange={(next) => onChange(next === 'all' ? undefined : next)}
        options={META_GOAL_FILTER_OPTIONS}
      />
      <p className="rpt-note rpt-muted">{GOAL_FILTER_NOTE}</p>
    </div>
  );
}
